import React from 'react';
import { Trophy } from 'lucide-react';
import {
  CHALLENGE_SCHEMA_VERSION,
  type ChallengeCriterion,
  type ChallengeStatus,
  isSimpleProgress,
  isTrendMetric,
} from './challengeTypes';
import SimpleProgressCriterion from './SimpleProgressCriterion';
import TrendMetricCriterion from './TrendMetricCriterion';

const ICON_MAP: Record<string, string> = {
  team_dinner: '🍴',
  trophy: '🏆',
  target: '🎯',
  flame: '🔥',
};

const resolveIcon = (id: string): React.ReactNode => {
  const emoji = ICON_MAP[id];
  if (emoji) return <span aria-hidden>{emoji}</span>;
  return <Trophy size={20} aria-hidden />;
};

const renderCriterion = (criterion: ChallengeCriterion, index: number): React.ReactNode => {
  if (isSimpleProgress(criterion)) {
    return <SimpleProgressCriterion key={index} data={criterion} />;
  }
  if (isTrendMetric(criterion)) {
    return <TrendMetricCriterion key={index} data={criterion} />;
  }
  return (
    <div
      key={index}
      data-testid="challenge-criterion-unknown"
      className="rounded-2xl bg-black/10 p-4 text-sm text-white/80"
    >
      Unsupported criterion type: {criterion.type}
    </div>
  );
};

interface Props {
  status: ChallengeStatus;
}

const ChallengeCard: React.FC<Props> = ({ status }) => {
  if (status.schema_version > CHALLENGE_SCHEMA_VERSION) {
    return (
      <div
        data-testid="challenge-card-unsupported-version"
        className="rounded-[20px] bg-gradient-to-br from-[#FF8A3D] to-[#E63333] p-5 text-white shadow-lg shadow-red-500/30"
      >
        <div className="flex items-center gap-2 font-display text-lg font-bold">
          <Trophy size={20} aria-hidden /> Newer challenge schema
        </div>
        <p className="mt-2 text-sm text-white/80">
          Update the app to view this challenge.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="challenge-card"
      className="rounded-[20px] bg-gradient-to-br from-[#FF8A3D] to-[#E63333] p-5 text-white shadow-lg shadow-red-500/30"
    >
      <header className="mb-4">
        <div className="flex items-center gap-2 font-display text-[19px] font-bold leading-tight">
          <span className="text-[22px]">{resolveIcon(status.icon)}</span>
          <span>{status.title}</span>
        </div>
        {status.subtitle ? (
          <p className="mt-1 text-[13px] font-medium text-white/[0.86]">
            {status.subtitle}
          </p>
        ) : null}
      </header>

      <div className="flex flex-col gap-3">
        {status.criteria.map(renderCriterion)}
      </div>

      {status.description ? (
        <p className="mt-4 text-[13px] font-semibold leading-snug text-white/[0.86]">
          {status.description}
        </p>
      ) : null}
    </div>
  );
};

export default ChallengeCard;
